import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  Tooltip,
  CircularProgress,
  Card,
  CardContent
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayArrow as PlayIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format, parseISO } from 'date-fns';
import { i18n } from '../../translate/i18n';
import { useKanbanTasks } from '../../hooks/useKanbanTasks';
import KanbanTaskModal from './KanbanTaskModal';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minHeight: '70vh',
    maxHeight: '85vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  taskCard: {
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px)',
    },
  },
  taskCardLow: {
    borderLeftColor: '#4caf50',
  },
  taskCardMedium: {
    borderLeftColor: '#ff9800',
  },
  taskCardHigh: {
    borderLeftColor: '#f44336',
  },
  taskCardUrgent: {
    borderLeftColor: '#9c27b0',
  },
  taskHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  taskTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
  },
  taskMeta: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  statusSelect: {
    minWidth: 120,
  },
  priorityChip: {
    fontWeight: 600,
  },
  progressSection: {
    marginTop: theme.spacing(1),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  assigneeAvatar: {
    width: 28,
    height: 28,
    fontSize: '0.875rem',
  },
  statusChip: {
    fontWeight: 500,
    minWidth: 100,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  }
}));

const TicketTasksModal = ({ open, onClose, ticketId, ticketName }) => {
  const classes = useStyles();
  const { tasks, loading, fetchTasks, updateTask, deleteTask } = useKanbanTasks(ticketId);
  const [editingTask, setEditingTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  useEffect(() => {
    if (open && ticketId) {
      fetchTasks({ ticketId });
    }
  }, [open, ticketId, fetchTasks]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handlePriorityChange = async (taskId, newPriority) => {
    try {
      await updateTask(taskId, { priority: newPriority });
    } catch (error) {
      console.error('Error updating task priority:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm(i18n.t('kanban.tasks.confirmDelete'))) {
      try {
        await deleteTask(taskId);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskModalOpen(true);
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setTaskModalOpen(true);
  };

  const handleTaskModalClose = () => {
    setTaskModalOpen(false);
    setEditingTask(null);
  };

  const handleTaskCreated = () => {
    fetchTasks({ ticketId });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800',
      high: '#f44336',
      urgent: '#9c27b0'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: '#757575',
      in_progress: '#2196f3',
      done: '#4caf50',
      blocked: '#f44336'
    };
    return colors[status] || colors.todo;
  };

  const getPriorityClass = (priority) => {
    const classes_map = {
      low: classes.taskCardLow,
      medium: classes.taskCardMedium,
      high: classes.taskCardHigh,
      urgent: classes.taskCardUrgent
    };
    return classes_map[priority] || classes.taskCardMedium;
  };

  const statusOptions = [
    { value: 'todo', label: i18n.t('kanban.tasks.status.todo'), icon: <RadioButtonUncheckedIcon /> },
    { value: 'in_progress', label: i18n.t('kanban.tasks.status.in_progress'), icon: <PlayIcon /> },
    { value: 'done', label: i18n.t('kanban.tasks.status.done'), icon: <CheckCircleIcon /> },
    { value: 'blocked', label: i18n.t('kanban.tasks.status.blocked'), icon: <FlagIcon /> },
  ];

  const priorityOptions = [
    { value: 'low', label: i18n.t('kanban.tasks.priority.low') },
    { value: 'medium', label: i18n.t('kanban.tasks.priority.medium') },
    { value: 'high', label: i18n.t('kanban.tasks.priority.high') },
    { value: 'urgent', label: i18n.t('kanban.tasks.priority.urgent') },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>
          <Box className={classes.header}>
            <Box>
              <Typography variant="h6" component="div">
                {i18n.t('kanban.tasks.ticketTasks')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {ticketName || `Ticket #${ticketId}`}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : tasks.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6" gutterBottom>
                {i18n.t('kanban.tasks.noTasks')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t('kanban.tasks.noTasksDescription')}
              </Typography>
            </Box>
          ) : (
            <Box>
              {tasks.map((task) => (
                <Card key={task.id} className={`${classes.taskCard} ${getPriorityClass(task.priority)}`}>
                  <CardContent>
                    <Box className={classes.taskHeader}>
                      <Box flex={1}>
                        <Typography className={classes.taskTitle}>
                          {task.title}
                        </Typography>
                        {task.description && (
                          <Typography variant="body2" color="textSecondary">
                            {task.description}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => handleEditTask(task)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteTask(task.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box className={classes.taskMeta}>
                      {/* Status */}
                      <FormControl size="small" className={classes.statusSelect}>
                        <Select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          variant="outlined"
                          renderValue={(value) => (
                            <Chip
                              size="small"
                              label={statusOptions.find(opt => opt.value === value)?.label}
                              style={{
                                backgroundColor: getStatusColor(value),
                                color: 'white'
                              }}
                              className={classes.statusChip}
                            />
                          )}
                        >
                          {statusOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {option.icon}
                                {option.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Priority */}
                      <FormControl size="small" style={{ minWidth: 100 }}>
                        <Select
                          value={task.priority}
                          onChange={(e) => handlePriorityChange(task.id, e.target.value)}
                          variant="outlined"
                          renderValue={(value) => (
                            <Chip
                              size="small"
                              label={priorityOptions.find(opt => opt.value === value)?.label}
                              style={{
                                backgroundColor: getPriorityColor(value),
                                color: 'white'
                              }}
                              className={classes.priorityChip}
                            />
                          )}
                        >
                          {priorityOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Due Date */}
                      {task.dueDate && (
                        <Chip
                          size="small"
                          icon={<ScheduleIcon />}
                          label={format(parseISO(task.dueDate), 'dd/MM/yyyy')}
                          variant="outlined"
                        />
                      )}

                      {/* Estimated Hours */}
                      {task.estimatedHours > 0 && (
                        <Chip
                          size="small"
                          icon={<ScheduleIcon />}
                          label={`${task.estimatedHours}h`}
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Assignees */}
                    {task.assignees && task.assignees.length > 0 && (
                      <Box display="flex" alignItems="center" gap={1} marginTop={2}>
                        <PersonIcon fontSize="small" color="action" />
                        <Box display="flex" gap={0.5}>
                          {task.assignees.map((assignee) => (
                            <Tooltip key={assignee.id} title={assignee.name}>
                              <Avatar
                                src={assignee.profileImage}
                                className={classes.assigneeAvatar}
                              >
                                {assignee.name.charAt(0)}
                              </Avatar>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Progress */}
                    {task.progress !== undefined && task.progress > 0 && (
                      <Box className={classes.progressSection}>
                        <Box display="flex" justifyContent="space-between" marginBottom={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t('kanban.tasks.progress')}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {task.progress}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={task.progress}
                          style={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            startIcon={<AddIcon />}
            onClick={handleCreateTask}
            color="primary"
            variant="contained"
          >
            {i18n.t('kanban.tasks.createTask')}
          </Button>
          <Button onClick={onClose}>
            {i18n.t('kanban.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Create/Edit Modal */}
      <KanbanTaskModal
        open={taskModalOpen}
        onClose={handleTaskModalClose}
        ticketId={ticketId}
        editingTask={editingTask}
        onTaskCreated={handleTaskCreated}
      />
    </>
  );
};

export default TicketTasksModal;
